import { User } from '@/models/user';
import { useAuth0 } from '@auth0/auth0-vue';

export function getAuthMethods() {
  const {
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    user,
    idTokenClaims,
    isAuthenticated,
  } = useAuth0();

  return {
    loginWithAudience: async () => {
      // Read query params from the current URL
      const searchParams = new URLSearchParams(window.location.search);
      const invitation = searchParams.get('invitation');
      const organization = searchParams.get('organization');

      const options: Parameters<typeof loginWithRedirect>[0] = {};

      // If the user came from an org invitation email,
      // pass the invitation + organization into Auth0
      if (invitation && organization) {
        options.authorizationParams = {
          invitation,
          organization,
        };
      }

      return loginWithRedirect(options);
    },

    logout: async (): Promise<void> => {
      clearUserIdCache();
      return auth0Logout();
    },

    getToken: async (): Promise<string> => {
      try {
        return await getAccessTokenSilently();
      } catch (error) {
        console.error('Get token silently auth failed, redirecting to login');
        await loginWithRedirect();
        throw new Error('Authentication required - redirecting to login');
      }
    },

    isLoggedIn: async (): Promise<boolean> => {
      return isAuthenticated.value;
    },

    getUserName: async (): Promise<string | undefined> => {
      return user.value?.email;
    },

    getUser: async (): Promise<User | undefined> => {
      const auth0User = user.value;
      if (!auth0User) {
        return undefined;
      }
      const { sub, name, email } = auth0User;
      const appUser: User = {
        id: sub,
        name: name ?? '',
        email: email ?? '',
      };
      return appUser;
    },

    getIDsForRecentWorkCards: (): string => {
      const userId = user.value?.sub ?? '';
      const orgId = idTokenClaims.value?.org_id ?? '';
      return userId.concat(orgId);
    },

    getUserId: async (): Promise<string> => {
      if (cachedUserId) {
        return cachedUserId;
      }
      // Try localStorage first (in case page was reloaded)
      const stored = localStorage.getItem(USER_ID_KEY);
      if (stored) {
        cachedUserId = stored;
        return stored;
      }
      // Otherwise, fetch from Auth0
      const auth0User = user.value;
      if (!auth0User?.sub) {
        throw new Error('Not authenticated');
      }
      cachedUserId = auth0User.sub;
      localStorage.setItem(USER_ID_KEY, auth0User.sub);
      return auth0User.sub;
    },

    getUserIdSync: (): string | null => {
      return cachedUserId;
    },

    clearUserIdCache: (): void => {
      cachedUserId = null;
      localStorage.removeItem(USER_ID_KEY);
    },
  };
}

// Constants and global variables
const USER_ID_KEY = 'sec-user-id';
// In-memory cache
let cachedUserId: string | null = null;

// Singleton management
let authInstance: ReturnType<typeof getAuthMethods> | null = null;

// For use outside of Vue components
export async function ensureAuthInitialized() {
  if (!authInstance) {
    console.warn('Auth should be initialized from a Vue component first');
  }
  return authInstance;
}

// Initialize from a component
export function initializeAuthSingleton() {
  if (!authInstance) {
    authInstance = getAuthMethods();
  }
  return authInstance;
}

// Export individual methods for backward compatibility
export async function login() {
  const auth = await ensureAuthInitialized();
  return auth?.loginWithAudience();
}

export async function getToken(): Promise<string> {
  const auth = await ensureAuthInitialized();
  return auth?.getToken() as Promise<string>;
}

export async function logout(): Promise<void> {
  const auth = await ensureAuthInitialized();
  return auth?.logout() as Promise<void>;
}

export async function isLoggedIn(): Promise<boolean> {
  const auth = await ensureAuthInitialized();
  return auth?.isLoggedIn() as Promise<boolean>;
}

export async function getUserName(): Promise<string | undefined> {
  const auth = await ensureAuthInitialized();
  return auth?.getUserName();
}

export async function getUser(): Promise<User | undefined> {
  const auth = await ensureAuthInitialized();
  return auth?.getUser();
}

export async function getIDsForRecentWorkCards(): Promise<string> {
  const auth = await ensureAuthInitialized();
  return auth?.getIDsForRecentWorkCards();
}

export async function getUserId(): Promise<string> {
  const auth = await ensureAuthInitialized();
  return auth?.getUserId() as Promise<string>;
}

export function getUserIdSync(): string | null {
  return cachedUserId;
}

export function clearUserIdCache(): void {
  cachedUserId = null;
  localStorage.removeItem(USER_ID_KEY);
}
